import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import Str from "shared/components/Str";
import OkPhotos from "shared/modules/OkPhotos";
import OkInstagram from "shared/modules/OkInstagram";
import SlidingPagination from "shared/components/SlidingPagination";

import {
	SECTIONS,
	USER_THEMES,
	BODY_WIDTH,
	PHOTO_PROMPT_POSITION,
	INSTAGRAM_PROMPT_POSITION,
	SECTIONS_WITHOUT_PAGINATION_FADES,
} from "apps/discovery/util/consts";

import BlankRow from "apps/discovery/components/BlankRow";
import PromptCard from "apps/discovery/components/PromptCard";

import "./Row.scss";

const placeholderCount = 10;

const _getUserTheme = (sectionId) => {
	let userTheme = USER_THEMES.gray;

	if (sectionId === SECTIONS.interests) {
		userTheme = USER_THEMES.blue;
	}

	return userTheme;
};

const Row = (props) => {
	const {
		row,
		sectionId,
		sizing,
		blankRowProps,
		error,
		isLoading,
		promos,
		placeholder,
		changePage,
	} = props;

	const userTheme = _getUserTheme(sectionId);
	const Template = props.template;
	const showUserSection = placeholder || !!row.data.length;
	const showBlankState = !placeholder && row.data && row.data.length === 0;

	const rowClasses = classnames({
		"discovery-row": true,
		hasError: error,
		isLoading,
	});

	const _onChangePage = (page) => {
		changePage({
			sectionId,
			page,
			rowId: row.id,
		});
	};

	let decoratedRow = [];

	// generate an array of children for the row, interspersing promo cards and
	// empty states as necessary
	if (!placeholder && row.data) {
		decoratedRow = row.data.map((user, i) =>
			(<Template
				key={user.userid}
				index={i}
				user={user}
				sectionId={sectionId}
				theme={userTheme}
			/>)
		);

		if (sectionId === SECTIONS.snapshots && promos.showPhotoUploadPromo) {
			decoratedRow.splice(PHOTO_PROMPT_POSITION, 0,
				<PromptCard
					key={`${PHOTO_PROMPT_POSITION}-prompt`}
					text={<Str>Let potential matches know what you’ve been up to.</Str>}
					ctaText={<Str>Post a pic</Str>}
					className="photo-upload"
					onClick={() => {
						OkPhotos().init({ referrer: "discovery" });
					}}
				/>
			);
		}

		if (sectionId === SECTIONS.instagram && promos.showInstagramPromo) {
			decoratedRow.splice(INSTAGRAM_PROMPT_POSITION, 0,
				<PromptCard
					key={`${INSTAGRAM_PROMPT_POSITION}-prompt`}
					text={<Str>Link your Instagram account so matches can scope you out.</Str>}
					ctaText={<Str>Connect</Str>}
					className="instagram"
					onClick={() => {
						// `true` here says that we want to connect for the purposes of
						// adding the feed to our public profile
						OkInstagram.connect(
							() => {
								window.location.reload();
							},
							true
						);
					}}
				/>
			);
		}

		if (sectionId === SECTIONS.interests) {
			const userCount = row.data.length;

			if (userCount > 0 && userCount < 4) {
				const userWidth = userCount * sizing.width + userCount * sizing.gutter;
				const width = BODY_WIDTH - userWidth;

				decoratedRow.push(
					<span
						key="interest-end-of-results"
						className="blank-row blank-row--blue interest-filler"
						style={{
							width,
							height: sizing.height,
							marginLeft: sizing.gutter,
						}}
					>
						<div className="blank-row-content">
							<span className="interest-filler-title">
								<Str>That’s everyone we could find.</Str>
							</span>
							<span className="interest-filler-body">
								<Str>Adjust your filters or search something else!</Str>
							</span>
						</div>
					</span>
				);
			}
		}
	}

	return (
		<div className={rowClasses}>
			{row.title &&
				<span className="discovery-row-title">
					{row.title}
				</span>
			}

			{showUserSection &&
				<SlidingPagination
					itemWidth={sizing.width}
					itemGutter={sizing.gutter}
					width={BODY_WIDTH}
					fadeOutSides={!SECTIONS_WITHOUT_PAGINATION_FADES[sectionId]}
					disabled={placeholder}
					previousButtonText={<Str>Previous people</Str>}
					nextButtonText={<Str>Next people</Str>}
					onPrevious={_onChangePage}
					onNext={_onChangePage}
					page={row.currentPage}
				>
					{placeholder && Array.from(Array(placeholderCount).keys()).map((i) =>
						(<Template
							key={i}
							theme={userTheme}
							sectionId={sectionId}
							placeholder
						/>)
					)}
					{decoratedRow}
				</SlidingPagination>
			}

			{showBlankState &&
				<BlankRow {...blankRowProps} />
			}

			{error &&
				<div className="discovery-row-error">
					<Str>Darn… there was a problem loading these people.</Str>
				</div>
			}
		</div>
	);
};

Row.propTypes = {
	row: PropTypes.object.isRequired,
	sectionId: PropTypes.string.isRequired,
	template: PropTypes.func.isRequired,
	sizing: PropTypes.object.isRequired,
	promos: PropTypes.object.isRequired,
	changePage: PropTypes.func.isRequired,
	blankRowProps: PropTypes.object,
	error: PropTypes.bool,
	placeholder: PropTypes.bool,
	isLoading: PropTypes.bool,
};

export default Row;



// WEBPACK FOOTER //
// ./src/apps/discovery/components/Row.jsx